import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Send, CheckCircle, Loader2 } from 'lucide-react';

export const QuickQuoteForm = ({ equipmentName }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          facility: '',
          service: `Orçamento - ${equipmentName}`,
          message: formData.message || `Gostaria de receber mais informações sobre ${equipmentName}.`
        }),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        // Google Ads Conversion Tracking
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-18096222373/RP-mCKjrrZ0cEKXh-bRD',
            'value': 1.0,
            'currency': 'BRL'
          });
        }
      } else {
        const data = await response.json();
        setErrorMessage(data.detail || 'Erro ao enviar. Tente novamente.');
        setStatus('error');
      }
    } catch (error) {
      setErrorMessage('Erro de conexão. Verifique sua internet.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-green-800 mb-2">
            Solicitação Enviada!
          </h3>
          <p className="text-green-700 text-sm">
            Recebemos seu interesse em {equipmentName}. Nossa equipe entrará em contato em breve.
          </p>
          <Button 
            variant="outline" 
            className="mt-4 border-green-500 text-green-600 hover:bg-green-100"
            onClick={() => setStatus('idle')}
          >
            Enviar Nova Solicitação
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
      <CardContent className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-1">
          Solicite um Orçamento
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Preencha seus dados e receba informações sobre {equipmentName}
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="text"
            name="name"
            placeholder="Seu nome *"
            value={formData.name}
            onChange={handleChange}
            required
            className="bg-white"
            data-testid="quick-quote-name"
          />
          <Input
            type="email"
            name="email"
            placeholder="Seu e-mail *"
            value={formData.email}
            onChange={handleChange}
            required
            className="bg-white"
            data-testid="quick-quote-email"
          />
          <Input
            type="tel"
            name="phone"
            placeholder="Seu telefone *"
            value={formData.phone}
            onChange={handleChange}
            required
            className="bg-white"
            data-testid="quick-quote-phone"
          />
          <Textarea
            name="message"
            placeholder="Mensagem (opcional)"
            value={formData.message}
            onChange={handleChange}
            rows={2}
            className="bg-white resize-none"
            data-testid="quick-quote-message"
          />
          
          {status === 'error' && (
            <p className="text-red-600 text-sm">{errorMessage}</p>
          )}
          
          <Button 
            type="submit" 
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            disabled={status === 'loading'}
            data-testid="quick-quote-submit"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Solicitar Orçamento
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
